/**
 * Cookie consent, kept in this browser (strictly necessary storage, so it
 * needs no consent itself). Signed-in users' choices are also logged on the
 * server as proof of consent (POST /policy-acceptances).
 *
 * Consent lapses -- and the banner asks again -- when the cookie policy version
 * changes or the choice is more than 12 months old.
 */

/** Bump a date when the corresponding policy text changes materially. */
export const POLICY_VERSIONS = {
  privacy: "2026-09-30",
  cookies: "2026-09-30",
} as const;

export interface CookieChoices {
  analytics: boolean;
}

interface StoredConsent {
  version: string;
  choices: CookieChoices;
  decided_at: string;
}

const STORAGE_KEY = "hms_cookie_consent";
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const CONSENT_CHANGED_EVENT = "cookie-consent-changed";
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

/** The current, still-valid choice, or null when the visitor must be asked. */
export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredConsent;
    if (stored.version !== POLICY_VERSIONS.cookies) return null;
    if (Date.now() - new Date(stored.decided_at).getTime() > MAX_AGE_MS) return null;
    return { ...stored, choices: { analytics: stored.choices?.analytics === true } };
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return readConsent()?.choices.analytics === true;
}

export function saveConsent(choices: CookieChoices): StoredConsent {
  const stored: StoredConsent = {
    version: POLICY_VERSIONS.cookies,
    choices: { analytics: choices.analytics === true },
    decided_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* storage blocked: the choice still applies for this page load */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: stored }));
  return stored;
}

/** Reopens the cookie banner in settings mode (footer links, policy pages). */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
